import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { getPublicationParsingHistory } from '../../../services/adminApi';
import type { ParsingHistoryResponseDto } from '../../../types/publication';

interface ParsingHistoryPanelProps {
  publicationId: number;
}

const ParsingHistoryPanel: React.FC<ParsingHistoryPanelProps> = ({ publicationId }) => {
  const { t, i18n } = useTranslation('admin');
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<ParsingHistoryResponseDto | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!token) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getPublicationParsingHistory(publicationId, token);
        setHistoryData(data);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError(t('publications.detail.parsingHistory.errors.notFound'));
        } else {
          setError(t('publications.detail.parsingHistory.errors.loadFailed'));
        }
        setHistoryData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [publicationId, token, t]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!historyData || historyData.parsingHistory.length === 0) {
    return <Alert severity="info">{t('publications.detail.parsingHistory.empty')}</Alert>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t('publications.detail.retry.parsingHistory')}</Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('publications.detail.parsingHistory.entry.date')}</TableCell>
              <TableCell>{t('publications.detail.parsingHistory.entry.status')}</TableCell>
              <TableCell align="right">{t('publications.detail.parsingHistory.entry.mentionsFound')}</TableCell>
              <TableCell align="right">{t('publications.detail.parsingHistory.entry.pageCount')}</TableCell>
              <TableCell align="right">{t('publications.detail.parsingHistory.entry.processingTime')}</TableCell>
              <TableCell>{t('publications.detail.parsingHistory.entry.errorMessage')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyData.parsingHistory.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  {new Date(entry.parsedAt).toLocaleString(i18n.language)}
                </TableCell>
                <TableCell>{entry.status}</TableCell>
                <TableCell align="right">{entry.mentionsFound}</TableCell>
                <TableCell align="right">{entry.pageCount ?? '—'}</TableCell>
                <TableCell align="right">
                  {entry.processingTimeMs != null ? `${entry.processingTimeMs} ms` : '—'}
                </TableCell>
                <TableCell>{entry.errorMessage ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ParsingHistoryPanel;
